import { ArrowBack, CheckCircleTwoTone, ExpandMore, KeyboardArrowLeft, KeyboardArrowRight, Search } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  InputAdornment,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from '@mui/material'
import { GreenText, LabelField, PhotoCard } from 'components'
import { useLayout } from 'context'
import { getVehicleDetail, getVehicleInfo, moveToStaging } from 'controllers/toDoLists/outboundTrips/index'
import { NeedsToBeMovedToStagingVehicleDetailDto, NeedsToBeMovedToStagingVehicleInfoDto } from 'dtos/toDoLists/outboundTrips'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { formatDateMoment } from 'utils'
import WarrantyCertPhotoCaptureDialog from './WarrantyCertPhotoCaptureDialog'

export default function NeedsToBeMovedToStaging() {
  const { setIsLayoutLoading } = useLayout()
  const { vehicleGuid } = useParams()
  const navigate = useNavigate()

  // 0 = Scan Bar Code, 1 = Warranty Cert Photo, 2 = Staging Review
  const [step, setStep] = useState<number>(0)
  const [vehicleInfo, setVehicleInfo] = useState<NeedsToBeMovedToStagingVehicleInfoDto | null>(null)
  const [vehicleDetails, setVehicleDetails] = useState<NeedsToBeMovedToStagingVehicleDetailDto | null>(null)
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [search, setSearch] = useState<string>('')

  // Step 1 - Warranty Cert Photo state
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [objectURL, setObjectURL] = useState<string | undefined>(undefined)

  // Step 2 - Staging Review State
  // None

  useEffect(() => {
    if (!vehicleGuid) return

    setIsLayoutLoading(true)
    getVehicleInfo(vehicleGuid)
      .then(res => setVehicleInfo(res.value))
      .finally(() => setIsLayoutLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleGuid])

  return (
    <Grid container spacing={2} alignItems='center'>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Grid container alignItems='center' justifyContent='space-between'>
              <Grid item>
                <Typography fontWeight='bold' variant='h6'>
                  Needs To Be Moved To Staging
                </Typography>
              </Grid>

              <Grid item>
                <Button color='primary' onClick={() => navigate(-1)} startIcon={<ArrowBack />} variant='text'>
                  BACK
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />} aria-controls='panel1-content' id='panel1-header'>
            <Typography fontWeight='bold' variant='h6'>
              {vehicleInfo?.vin ?? '-'}
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Grid container spacing={1} alignItems='center'>
              <Grid item xs={12}>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Exporter' value={vehicleInfo?.exporterCompany} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Year' value={vehicleInfo?.year} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Make' value={vehicleInfo?.make} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Model' value={vehicleInfo?.model} />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Importer Stock #' value={vehicleInfo?.importerStockNumber} />
              </Grid>

              <Grid item xs={12}>
                <GreenText variant='outlined'>
                  {`**Vehicle Information Card Accordion:**

Clicking the Vehicle Information card will expanding the accordion displaying additional details about the vehicle.

**VIN:**

Displays the VIN (Vehicle Identification Number) associated with the vehicle.

**Exporter:**

Displays the Exporter Company associated with the vehicle.

**Year:**

Displays the year of manufacture for the vehicle.

**Make:**

Displays the make (manufacturer) of the vehicle.

**Model:**

Displays the model of the vehicle.
 
**Importer Stock Number:**

Displays the Importer Stock Number associated with the vehicle.`}
                </GreenText>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Grid container spacing={2} alignItems='center' justifyContent='flex-start'>
              <Grid item xs={12}>
                <Stepper alternativeLabel activeStep={step}>
                  <Step>
                    <StepLabel>Scan Bar Code</StepLabel>
                  </Step>

                  <Step>
                    <StepLabel>Warranty Cert Photo</StepLabel>
                  </Step>

                  <Step>
                    <StepLabel>Staging Review</StepLabel>
                  </Step>
                </Stepper>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {step === 0 && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      disabled={isSearching}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <Search />
                          </InputAdornment>
                        )
                      }}
                      label='Last 6 of VIN/Importer Stock #'
                      onChange={e => setSearch(e.target.value)}
                      value={search}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <LoadingButton
                      color='primary'
                      disabled={search.length < 6}
                      fullWidth
                      loading={isSearching}
                      onClick={() => {
                        if (!vehicleGuid) return
                        setIsSearching(true)
                        getVehicleDetail(vehicleGuid, search)
                          .then(res => {
                            enqueueSnackbar('Match Found!', { variant: 'success' })
                            setVehicleDetails(res.value)
                            setSearch('')
                          })
                          .finally(() => setIsSearching(false))
                      }}
                      variant='contained'
                    >
                      SEARCH
                    </LoadingButton>
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='VIN' value={vehicleDetails?.vin} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Year' value={vehicleDetails?.year} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Make' value={vehicleDetails?.make} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Model' value={vehicleDetails?.model} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Exporter/Buyer' value={vehicleDetails?.buyerExporterCompany} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Ready For Outbound Date' value={formatDateMoment(vehicleDetails?.readyForOutboundDateTime)} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Vehicle Release Date' value={formatDateMoment(vehicleDetails?.vehicleReleaseDateTime)} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Bond Release Date' value={formatDateMoment(vehicleDetails?.bondReleaseDateTime)} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Outbound Trip #' value={vehicleDetails?.outboundTripNumber} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Outbound Trip Date' value={formatDateMoment(vehicleDetails?.outboundTripDate)} />
                  </Grid>

                  <Grid item xs={12}>
                    <Button color='primary' disabled={!vehicleDetails} fullWidth onClick={() => setStep(1)} variant='contained'>
                      PREP VEHICLE FOR OUTBOUND TRIP
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Vehicle Steps:**

Displays the current step of the vehicle in the Needs To Be Moved To Staging Process.

**Last 6 of VIN/Importer Stock Number Search:**

A free-text input field for entering either the last six characters of the VIN or the Importer Stock Number of a vehicle.

**SEARCH Button:**

Clicking the Search button performs a check to match the entered Last 6 of VIN or Importer Stock Number with the currently selected vehicle.

If a match is found, the VIN, Year, Make, and Model fields will auto-populate with the vehicle’s details.

If no match is found, red text will appear, displaying: “No Match Found.”

**VIN:**

A read-only label that displays the VIN of the searched vehicle.

By default, the field displays a “-” if no vehicle has been found.

**Year:**

A read-only label that displays the Year of the searched vehicle.

By default, the field displays a “-” if no vehicle has been found.

**Make:**

A read-only label that displays the Make of the searched vehicle.

By default, the field displays a “-” if no vehicle has been found.

**Model:**

A read-only label that displays the Model of the searched vehicle.

By default, the field displays a “-” if no vehicle has been found.

**Exporter/Buyer:**

Displays the Buyer company name if one is associated with the vehicle. If no Buyer is assigned, the Exporter company name is shown instead. 

**Ready For Outbound Date:**

Read from the Conformity Details card under Vehicle Details – Conformity (tab). This date allows vehicles to be added to Outbound Trips before the Vehicle Release Date is reached (the 30-day DOT hold expires).

**Vehicle Release Date:**

Read from the Conformity Details card under Vehicle Details – Conformity (tab). This is the date that the DOT mandated vehicle hold expires (typically 30 days after submission).

Bond Release Date:

Read from the Outbound Trip card under Vehicle Details – Conformity (tab). This field is nullable and may not be present for all vehicles. If the Bond Release Date is a past or current date, the vehicle can immediately be added to an Outbound Trip, regardless of the Ready For Outbound Date or Vehicle Release Date.

**Outbound Trip #:**

Displays the unique identifier of the Outbound Trip to which the vehicle is assigned.

**Outbound Trip Date:**

Displays the scheduled date of the Outbound Trip.

**PREP VEHICLE FOR OUTBOUND TRIP Button:**

Clicking this button will:

   - Save the name of the user who pressed the button along with the date and time to the database.
   - Advance the user to the next step in the process.

This button is only enabled if the search for the Last 6 of VIN or Importer Stock Number results in a match.`}
                    </GreenText>
                  </Grid>
                </>
              )}

              {step === 1 && (
                <>
                  <WarrantyCertPhotoCaptureDialog
                    initialValues={vehicleDetails!}
                    isLoading={false}
                    onClose={() => setDialogOpen(false)}
                    onSave={values => {
                      setVehicleDetails({ ...vehicleDetails, ...values })
                      setDialogOpen(false)
                      if (objectURL) URL.revokeObjectURL(objectURL)
                      setObjectURL(URL.createObjectURL(values.warrantyCertificateInGloveBoxFile!))
                    }}
                    open={dialogOpen}
                  />

                  <Grid item xs={12} container alignItems='center' justifyContent='center'>
                    <Grid item>
                      <PhotoCard
                        label='Warranty Certificate In Glove Box'
                        onClick={() => setDialogOpen(true)}
                        src={objectURL}
                        complete={Boolean(vehicleDetails?.warrantyCertificateInGloveBoxFile)}
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      disabled={!vehicleDetails?.warrantyCertificateInGloveBoxFile}
                      fullWidth
                      startIcon={<KeyboardArrowRight />}
                      onClick={() => setStep(2)}
                      variant='contained'
                    >
                      NEXT
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button fullWidth startIcon={<KeyboardArrowLeft />} onClick={() => setStep(0)} variant='contained'>
                      PREVIOUS
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Vehicle Steps:**

Displays the current step of the vehicle in the Needs To Be Moved To Staging Process.

**Warrant Certificate In Glove Box:**

Displays the Warranty Certificate In Glove Box image uploaded by the user.

If no image has been uploaded, a “No Image” icon will be displayed instead.
 
Clicking the Warranty Certificate In Glove Box will open the Capture Warranty Certificate In Glove Box Photo Dialog.

**NEXT Button:**

Clicking this button will:

   - Save the uploaded photo to the database.
   - Advance the user to the next step in the process.

This button remains disabled until a photo has been uploaded.
 
**PREVIOUS Button:**

Clicking this button will:

   - Discard any uploaded photo that has not been saved.
   - Return the user to the previous step in the process`}
                    </GreenText>
                  </Grid>
                </>
              )}

              {step === 2 && (
                <>
                  <Grid item xs={12} container spacing={1} alignItems='center' justifyContent='flex-start'>
                    <Grid item>
                      <CheckCircleTwoTone color='success' />
                    </Grid>

                    <Grid item>
                      <Typography variant='body1'>Warranty Certificate In Glovebox</Typography>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} container spacing={1} alignItems='center' justifyContent='flex-start'>
                    <Grid item>
                      <CheckCircleTwoTone color='success' />
                    </Grid>

                    <Grid item>
                      <Typography variant='body1'>
                        Parking Space:{' '}
                        <Typography component='span' fontWeight='bold'>
                          {vehicleDetails?.parkingSpace}
                        </Typography>
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      onClick={() => {
                        setIsLayoutLoading(true)
                        moveToStaging(vehicleDetails!)
                          .then(() => {
                            enqueueSnackbar('Vehicle Moved To Staging Successfully!', { variant: 'success' })
                            navigate('/to-do-list', { replace: true })
                          })
                          .finally(() => setIsLayoutLoading(false))
                      }}
                      variant='contained'
                    >
                      SUBMIT
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button fullWidth startIcon={<KeyboardArrowLeft />} onClick={() => setStep(1)} variant='contained'>
                      PREVIOUS
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Vehicle Check-In Steps:**

Displays the current step of the vehicle in the process.

**Warranty Certificate In Glove Box Check Mark:**

Displays a check mark to indicate that the Warranty Certificate In Glovebox Photo has been successfully uploaded.

**Parking Space Check Mark:**

Displays a check mark to indicate that a Parking space has been selected and displays the parking space.

**SUBMIT Button:**

Clicking this button will:

Upload the Warranty Certificate In Glovebox and associate it with the vehicle record, including:

   - The name of the user who completed the process.
   - A date/time stamp.

Save/Update the vehicles parking space to the selected parking space.

Return the user to the Needs To Be Moved To Staging to-do list screen.

**PREVIOUS Button:**

Clicking this button will:

   - Return the user to the previous step in the process.`}
                    </GreenText>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )
}
